/** Blu design tokens — fallback when merchant branding is unset. */
export const BLU_BRAND_DEFAULTS = {
  primary: '1976d2',
  secondary: '1d1d1d',
  negative: 'e74c3c',
  fontFamily: 'Inter'
}

/**
 * Strip # and alpha channel for DvPayLite customUI hex fields.
 * @param {string|null|undefined} color
 * @returns {string}
 */
export function stripHexForDvPay (color) {
  if (!color || typeof color !== 'string') {
    return ''
  }
  let hex = color.trim().replace(/^#/, '')
  if (hex.length === 8) {
    hex = hex.slice(0, 6)
  }
  return hex.toLowerCase()
}

/**
 * @param {object|null|undefined} businessInfo
 */
export function resolveDvPayLiteBranding (businessInfo = null) {
  const primary = stripHexForDvPay(businessInfo?.primary_color) || BLU_BRAND_DEFAULTS.primary
  const secondary = stripHexForDvPay(businessInfo?.accent_color) || BLU_BRAND_DEFAULTS.secondary
  return {
    primary,
    secondary,
    negative: BLU_BRAND_DEFAULTS.negative,
    fontFamily: BLU_BRAND_DEFAULTS.fontFamily
  }
}

/**
 * @param {ReturnType<typeof resolveDvPayLiteBranding>} branding
 */
export function buildCustomUi (branding) {
  return {
    fontFamily: branding.fontFamily,
    primaryColor: branding.primary,
    secondaryColor: branding.secondary,
    negativeButtonColor: branding.negative,
    removeLoaderLogo: 'YES',
    requiredAvs: 'NO'
  }
}

/**
 * Merge customUI (and optional IsvId) into any DvPayLite JSON payload.
 *
 * @param {object} payload
 * @param {object} [options]
 * @param {object|null} [options.businessInfo]
 * @param {string|null} [options.isvId]
 */
export function enrichDvPayLitePayload (payload, { businessInfo = null, isvId = null } = {}) {
  const enriched = { ...(payload || {}) }
  if (!enriched.applicationType) {
    enriched.applicationType = 'DVPAYLITE'
  }
  enriched.customUI = buildCustomUi(resolveDvPayLiteBranding(businessInfo))
  if (isvId && !enriched.IsvId) {
    enriched.IsvId = String(isvId)
  }
  return enriched
}

/**
 * Device register / addTerminal payload with customUI included.
 *
 * @param {object} params
 * @param {string} params.tpn
 * @param {object|null} [params.businessInfo]
 * @param {string|null} [params.isvId]
 */
export function buildRegisterPayload ({ tpn, businessInfo = null, isvId = null }) {
  return enrichDvPayLitePayload({
    tpn: String(tpn || '').trim(),
    applicationType: 'DVPAYLITE'
  }, { businessInfo, isvId })
}

/**
 * Parse and enrich a deeplink `data` JSON string if possible.
 *
 * @param {string|undefined|null} dataJson
 * @param {object} [options]
 * @returns {string|undefined|null}
 */
export function enrichDvPayLiteDataParam (dataJson, options = {}) {
  if (typeof dataJson !== 'string' || !dataJson.trim()) {
    return dataJson
  }
  try {
    const parsed = JSON.parse(dataJson)
    return JSON.stringify(enrichDvPayLitePayload(parsed, options))
  } catch {
    return dataJson
  }
}
